import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { confirmAlert } from "react-confirm-alert";
import { ApiError } from "../../Api/ApiError";
import { DeleteEquipmentData, getEquipmentList, RegRepairedCompleteEquipment, RegRepairedEquipment } from "../../Api/Equipment";
import { EquipmentCategory } from "../../Objects/EquipmentCategory";
import { EquipmentData } from "../../Objects/EquipmentData";
import { StoreObjectStore } from "../../ObjectStore/StoreObjectStore";
import EquipmentCard from "../molecules/EquipmentCard";
import EquipmentEditDialog from "./EquipmentEditDialog";

export interface EquipmentDataListProps {
    equipmentCategory: EquipmentCategory
    hiddenDeleteEquip?: boolean
}

const EquipmentDataList: React.FC<EquipmentDataListProps> = ({
    equipmentCategory,
    hiddenDeleteEquip
}) => {
    let { enqueueSnackbar } = useSnackbar()
    const { storeObjectState } = React.useContext(StoreObjectStore)

    const [equipmentDataList, setEquipmentDataList] = React.useState<EquipmentData[]>([])
    const [equipmentDataListDisp, setEquipmentDataListDisp] = React.useState<EquipmentData[]>([])

    const [showEquipmentEditDialog, setShowEquipmentEditDialog] = React.useState<boolean>(false)
    const [editEquipment, setEditEquipment] = React.useState<EquipmentData>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadEquipmentList = React.useCallback(() => {
        if (storeObjectState.storeData?.str_id && equipmentCategory.equcate_id) {
            getEquipmentList({
                str_id: storeObjectState.storeData.str_id,
                equcate_id: equipmentCategory.equcate_id
            }, {signal: abortController.signal}).then(result => {
                setEquipmentDataList(result.list)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, equipmentCategory, storeObjectState.storeData?.str_id, enqueueSnackbar])

    const doDelete = React.useCallback((equipmentData: EquipmentData) => {
        if (equipmentData.equ_id) {
            DeleteEquipmentData({
                equ_id: equipmentData.equ_id
            }, {signal: abortController.signal}).then(() => {
                loadEquipmentList()
                enqueueSnackbar("削除しました", { variant: "success" })
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        } else {
            enqueueSnackbar("登録に失敗しました。", { variant: "error" })
        }
    }, [abortController, enqueueSnackbar, loadEquipmentList])

    const doRepaire = React.useCallback((equipmentData: EquipmentData) => {
        if (equipmentData.equ_id) {
            if (equipmentData.repaired_flug) {
                RegRepairedCompleteEquipment({
                    equ_id: equipmentData.equ_id
                }, {signal: abortController.signal}).then(() => {
                    loadEquipmentList()
                    enqueueSnackbar("修理完了登録が完了しました", { variant: "success" })
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
            } else {
                RegRepairedEquipment({
                    equ_id: equipmentData.equ_id
                }, {signal: abortController.signal}).then(() => {
                    loadEquipmentList()
                    enqueueSnackbar("修理登録が完了しました", { variant: "success" })
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
            }
        }
    }, [abortController, enqueueSnackbar, loadEquipmentList])

    React.useEffect(() => {
        loadEquipmentList()
    }, [loadEquipmentList])

    React.useEffect(() => {
        if (hiddenDeleteEquip) {
            let disp = equipmentDataList.filter(equipmentData => equipmentData.delete_flug === false)
            setEquipmentDataListDisp(disp)
        } else {
            setEquipmentDataListDisp(equipmentDataList)
        }
    }, [equipmentDataList, hiddenDeleteEquip])

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    mb: 1
                }}
            >
                <Typography
                    sx={{
                        fontWeight: "bold",
                        pl: 2,
                    }}
                >{equipmentCategory.equcate_name}</Typography>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                        setEditEquipment({
                            equcate_id: equipmentCategory.equcate_id
                        })
                        setShowEquipmentEditDialog(true)
                    }}
                >機材を追加</Button>
            </Stack>
            <Divider
                sx={{
                    mb: 2
                }}
            />
            <Grid container
                spacing={2}
                alignItems="stretch"
            >
                {equipmentDataListDisp.map(equipmentData => (
                    <Grid item xs={12} sm={6} md={4} key={equipmentData.equ_id}>
                        <EquipmentCard
                            equipmentData={equipmentData}
                            onEdit={(equipmentData) => {
                                setEditEquipment(equipmentData)
                                setShowEquipmentEditDialog(true)
                            }}
                            onDelete={(equipmentData) => {
                                confirmAlert({
                                    message: "機材を削除します。この操作は取り消すことができません。",
                                    buttons: [
                                        {
                                            label: "キャンセル",
                                            onClick: () => { }
                                        },
                                        {
                                            label: "実行",
                                            onClick: () => {
                                                doDelete(equipmentData)
                                            }
                                        }
                                    ]
                                })
                            }}
                            onRepair={(equipmentData) => {
                                doRepaire(equipmentData)
                            }}
                        />
                    </Grid>
                ))}
                {storeObjectState.storeData?.str_id &&
                    <EquipmentEditDialog
                        open={showEquipmentEditDialog}
                        onClose={isEdit => {
                            if (isEdit) {
                                loadEquipmentList()
                            }
                            setShowEquipmentEditDialog(false)
                        }}
                        equipmentData={editEquipment}
                        str_id={storeObjectState.storeData?.str_id}
                    />
                }
            </Grid>
        </Box>
    )
}

export default EquipmentDataList